/**
 * Interfejsy i typy dla systemu logowania zdarzeń
 *
 * Zawiera definicje wszystkich typów związanych z logowaniem działań użytkowników
 * w aplikacji registration. Logi są przechowywane w kolekcji Firestore 'logs'
 * i mogą być przeglądane w aplikacji admin.
 */

/**
 * Typy zdarzeń - konkretne akcje wykonywane przez użytkowników
 * Używane do efektywnego filtrowania w aplikacji admin
 */
export enum EventType {
  // Uwierzytelnianie
  USER_LOGIN = "USER_LOGIN",
  USER_REGISTRATION = "USER_REGISTRATION",
  USER_LOGOUT = "USER_LOGOUT",
  USER_PASSWORD_CHANGE = "USER_PASSWORD_CHANGE",

  // Zawodnicy
  PARTICIPANT_CREATE = "PARTICIPANT_CREATE",
  PARTICIPANT_UPDATE = "PARTICIPANT_UPDATE",
  PARTICIPANT_DELETE = "PARTICIPANT_DELETE",

  // Zgłoszenia
  APPLICATION_CREATE = "APPLICATION_CREATE",
  APPLICATION_UPDATE = "APPLICATION_UPDATE",
  APPLICATION_DELETE = "APPLICATION_DELETE",
  APPLICATION_STATUS_CHANGE = "APPLICATION_STATUS_CHANGE",

  // Prezentacje
  PRESENTATION_CREATE = "PRESENTATION_CREATE",
  PRESENTATION_UPDATE = "PRESENTATION_UPDATE",
  PRESENTATION_DELETE = "PRESENTATION_DELETE",
}

/**
 * Typy akcji - ogólne kategorie operacji
 * Używane do szczegółów w logach
 */
export enum LogAction {
  CREATE = "CREATE",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
  LOGIN = "LOGIN",
  REGISTER = "REGISTER",
  LOGOUT = "LOGOUT",
}

/**
 * Typy encji - obiekty na których wykonywane są operacje
 * Używane do szczegółów w logach
 */
export enum EntityType {
  USER = "USER",
  APPLICATION = "APPLICATION",
  PRESENTATION = "PRESENTATION",
  PARTICIPANT = "PARTICIPANT",
}

/**
 * Główny interfejs dokumentu logu w Firestore
 *
 * Zawiera wszystkie informacje potrzebne do:
 * - Identyfikacji użytkownika i organizacji
 * - Filtrowania po typie zdarzenia
 * - Śledzenia zmian (before/after state)
 * - Analizy błędów i sukcesów
 */
export interface LogEntry {
  /** Unikalny identyfikator logu (generowany przez Firestore) */
  id: string;

  /** Timestamp utworzenia logu */
  timestamp: Date;

  /** Dane użytkownika wykonującego akcję */
  userId: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userOrganizationName: string;

  /** Typ zdarzenia - używany do filtrowania */
  eventType: EventType;

  /** Szczegóły akcji i encji */
  action: LogAction;
  entityType: EntityType;
  entityId: string;

  /** Stany przed i po operacji - dla śledzenia zmian */
  beforeState: any;
  afterState: any;

  /** Informacje o powodzeniu operacji */
  success: boolean;
  errorMessage?: string;

  /** Dodatkowe metadane (contestId, categoryId, etc.) */
  metadata?: {
    contestId?: string;
    contestName?: string;
    applicationId?: string;
    categoryId?: string;
    presentationId?: string;
    participantId?: string;
    oldStatus?: string;
    newStatus?: string;
    [key: string]: any;
  };
}

/**
 * Typ danych wejściowych do funkcji logowania
 * Pomija pola generowane automatycznie (id, timestamp)
 */
export type LogData = Omit<LogEntry, "id" | "timestamp">;

/**
 * Typ danych wejściowych do funkcji logowania z kontekstem użytkownika
 * Pomija pola użytkownika (są dodawane automatycznie z kontekstu)
 */
export type LogDataWithUser = Omit<
  LogData,
  "userId" | "userEmail" | "userFirstName" | "userLastName" | "userOrganizationName"
>;

/**
 * Typ danych wejściowych do funkcji logowania bez kontekstu użytkownika
 * Wymaga podania wszystkich danych użytkownika
 */
export type LogDataWithFullUser = Omit<
  LogData,
  "userId" | "userEmail" | "userFirstName" | "userLastName" | "userOrganizationName"
> & {
  userId: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userOrganizationName: string;
};

/**
 * Typ odpowiedzi z funkcji logowania
 * Zawiera informacje o powodzeniu operacji logowania
 */
export interface LoggingResult {
  success: boolean;
  logId?: string;
  error?: string;
}

/**
 * Typ konfiguracji logowania
 * Używany do konfiguracji zachowania systemu logowania
 */
export interface LoggingConfig {
  /** Czy logować błędy do konsoli */
  logErrorsToConsole: boolean;

  /** Czy rzucać wyjątki przy błędach logowania */
  throwOnError: boolean;

  /** Maksymalny rozmiar beforeState/afterState w bajtach */
  maxStateSize: number;

  /** Czy kompresować duże stany */
  compressLargeStates: boolean;
}

/**
 * Domyślna konfiguracja logowania
 */
export const DEFAULT_LOGGING_CONFIG: LoggingConfig = {
  logErrorsToConsole: true,
  throwOnError: false,
  maxStateSize: 1024 * 1024, // 1MB
  compressLargeStates: true,
};
